import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, FINANCE_NAME } from '../../config';
import './FinanceMeeting.css';

const FinanceMeeting = () => {
  const [balanceData, setBalanceData] = useState(null);
  const [outstandingLoan, setOutstandingLoan] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [loanLoading, setLoanLoading] = useState(false);
  const [error, setError] = useState('');

  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const getOutstandingAmount = (row) => toNumber(row.outstanding_balance ?? row.remaining_amount);

  const formatCurrency = (value) => {
    const amount = toNumber(value);
    return `₹ ${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fetchSecondaryBalance = async () => {
    setBalanceLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/secondary-balance-enquiry`);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || 'Failed to fetch secondary balance data');
      }

      const data = await response.json();
      setBalanceData(data);
    } catch (err) {
      console.error('Failed to fetch secondary balance for finance meeting:', err);
      setError(err.message || 'Failed to load secondary balance data.');
      setBalanceData(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  const fetchOutstandingLoan = async () => {
    setLoanLoading(true);

    try {
      const partnersRes = await axios.get(`${API_BASE_URL}/partners`, { timeout: 60000 });
      const partners = Array.isArray(partnersRes.data)
        ? partnersRes.data.map((p) => (typeof p === 'string' ? p.trim() : '')).filter(Boolean)
        : [];

      if (!partners.length) {
        setOutstandingLoan(0);
        return;
      }

      const summaries = await Promise.all(
        partners.map(async (partner) => {
          const partnerEnc = encodeURIComponent(partner);
          const [glDueR, cdDueR, tbdDueR] = await Promise.allSettled([
            axios.get(`${API_BASE_URL}/api/general-loan-partnerwise-due-report?partner=${partnerEnc}`, {
              timeout: 60000,
            }),
            axios.get(`${API_BASE_URL}/api/cd-loan-partnerwise-due-report?partner=${partnerEnc}`, {
              timeout: 60000,
            }),
            axios.get(`${API_BASE_URL}/api/tbd-loan-partnerwise-due-report?partner=${partnerEnc}`, {
              timeout: 60000,
            }),
          ]);

          const glDue =
            glDueR.status === 'fulfilled' && Array.isArray(glDueR.value.data) ? glDueR.value.data : [];
          const cdDue =
            cdDueR.status === 'fulfilled' && Array.isArray(cdDueR.value.data) ? cdDueR.value.data : [];
          const tbdDue =
            tbdDueR.status === 'fulfilled' && Array.isArray(tbdDueR.value.data) ? tbdDueR.value.data : [];

          const generalLoan = glDue.reduce((sum, row) => sum + getOutstandingAmount(row), 0);
          const cdLoan = cdDue.reduce((sum, row) => sum + getOutstandingAmount(row), 0);
          const tbdLoan = tbdDue.reduce((sum, row) => sum + getOutstandingAmount(row), 0);

          return generalLoan + cdLoan + tbdLoan;
        })
      );

      setOutstandingLoan(summaries.reduce((sum, value) => sum + toNumber(value), 0));
    } catch (err) {
      console.error('Failed to fetch outstanding loan for finance meeting:', err);
      setError((prev) => prev || 'Failed to load outstanding loan from partner meeting.');
      setOutstandingLoan(0);
    } finally {
      setLoanLoading(false);
    }
  };

  useEffect(() => {
    fetchSecondaryBalance();
    fetchOutstandingLoan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableOne = useMemo(() => {
    const partnerTransactions = toNumber(balanceData?.secondaryPartnerCredits);
    const totalInterestAndOd =
      toNumber(balanceData?.secondaryGeneralLoanInterest) +
      toNumber(balanceData?.secondaryCdLoanInterest) +
      toNumber(balanceData?.secondaryTbdLoanInterest) +
      toNumber(balanceData?.secondaryGlOverdue) +
      toNumber(balanceData?.secondaryCdOverdue) +
      toNumber(balanceData?.secondaryTbdOverdue);
    const documentCharges = toNumber(balanceData?.secondaryDocumentChargesCredit);
    const rdCardValue = toNumber(balanceData?.secondaryRdCredit);
    const total = partnerTransactions + totalInterestAndOd + documentCharges + rdCardValue;

    return [
      { label: 'Partner Transactions', value: partnerTransactions },
      { label: 'Total Interest and OD', value: totalInterestAndOd },
      { label: 'Document charges', value: documentCharges },
      { label: 'RD card value', value: rdCardValue },
      { label: 'Total', value: total, isTotal: true },
    ];
  }, [balanceData]);

  const tableTwo = useMemo(() => {
    const luckyDraw = toNumber(balanceData?.secondaryRdDebit);
    const expense = toNumber(balanceData?.secondaryExpenseDebit);
    const total = outstandingLoan + luckyDraw + expense;

    return [
      { label: 'Outstanding loan', value: outstandingLoan },
      { label: 'Lucky draw', value: luckyDraw },
      { label: 'Expense', value: expense },
      { label: 'Total', value: total, isTotal: true },
    ];
  }, [balanceData, outstandingLoan]);

  const handlePrint = () => {
    const printContent = document.getElementById('finance-meeting-print-area')?.innerHTML;
    if (!printContent) {
      return;
    }

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Finance Meeting - ${FINANCE_NAME}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @page { size: A4; margin: 1.5cm 1.2cm; }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              color: #1a202c;
            }
            h1 {
              text-align: center;
              font-size: 20px;
              margin-bottom: 6px;
              text-transform: uppercase;
            }
            h2 {
              text-align: center;
              font-size: 16px;
              margin-bottom: 6px;
            }
            .print-meta {
              text-align: center;
              font-size: 13px;
              margin-bottom: 20px;
              color: #333;
            }
            .finance-meeting-tables {
              display: block;
            }
            .finance-meeting-table-card {
              margin-bottom: 24px;
              page-break-inside: avoid;
            }
            .finance-meeting-table-title {
              font-size: 15px;
              font-weight: bold;
              margin-bottom: 8px;
              color: #1a202c;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;
            }
            th, td {
              border: 1px solid #ccc;
              padding: 10px 12px;
            }
            th {
              background-color: #0476df;
              color: #fff;
              text-align: left;
            }
            td:last-child, th:last-child {
              text-align: right;
            }
            .total-row td {
              font-weight: bold;
              background: #e8f4ff;
            }
          </style>
        </head>
        <body>
          <h1>${FINANCE_NAME}</h1>
          <h2>Finance Meeting</h2>
          <p class="print-meta">Period: Entire Duration</p>
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const loading = balanceLoading || loanLoading;

  return (
    <div className="finance-meeting-page">
      <div className="finance-meeting-container">
        <h2 className="finance-meeting-heading">Finance Meeting</h2>
        <p className="finance-meeting-subtitle">Summary from Secondary Balance & Partner Meeting</p>

        {loading ? (
          <div className="finance-meeting-status">Loading finance meeting data…</div>
        ) : error && !balanceData ? (
          <div className="finance-meeting-error">{error}</div>
        ) : (
          <>
            {error ? <div className="finance-meeting-error soft">{error}</div> : null}

            <div className="finance-meeting-print-actions">
              <button
                type="button"
                className="print-button"
                onClick={handlePrint}
                disabled={!balanceData}
              >
                Print
              </button>
            </div>

            <div id="finance-meeting-print-area" className="finance-meeting-tables">
              <div className="finance-meeting-table-card">
                <h3 className="finance-meeting-table-title">Credits Summary</h3>
                <table className="finance-meeting-table">
                  <thead>
                    <tr>
                      <th>Particulars</th>
                      <th>Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableOne.map((row) => (
                      <tr key={row.label} className={row.isTotal ? 'total-row' : undefined}>
                        <td>{row.label}</td>
                        <td>{formatCurrency(row.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="finance-meeting-table-card">
                <h3 className="finance-meeting-table-title">Debits Summary</h3>
                <table className="finance-meeting-table">
                  <thead>
                    <tr>
                      <th>Particulars</th>
                      <th>Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableTwo.map((row) => (
                      <tr key={row.label} className={row.isTotal ? 'total-row' : undefined}>
                        <td>{row.label}</td>
                        <td>{formatCurrency(row.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FinanceMeeting;
